import { useState } from 'react';
import { Navigate, useOutletContext } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileDown, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import { downloadVersion, useDocuments } from '@/lib/documents';
import { addDays, todayIn } from '@/lib/reports';
import type { Program } from '@/lib/types';
import { cn, fmtDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Field, Input, Select } from '@/components/ui/field';
import { Alert, Badge, Card, PageHeader, Spinner } from '@/components/ui/misc';
import { Dialog } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';

interface Engagement {
  agreementName: string;
  effectiveDate: string;
  terminationNoticeDays: number;
  nonRenewalNoticeDays: number;
  paymentTermsDays: number;
  invoiceDayOfMonth: number;
  status: {
    term: number;
    termStart: string;
    termEnd: string;
    nextRenewal: string;
    nonRenewalNoticeBy: string;
    daysToNoticeDeadline: number;
  };
}
interface Entry {
  id: string;
  date: string;
  description: string;
  sourceType: string | null;
  excluded: boolean;
  manual: boolean;
}
interface Expense {
  id: string;
  description: string;
  amount: string;
  status: 'REQUESTED' | 'APPROVED' | 'DECLINED' | 'REIMBURSED';
  requestedAt: string;
  approvedByName: string | null;
  approvedAt: string | null;
  approvalDocumentId: string | null;
  receiptDocumentId: string | null;
}
interface Month {
  month: string;
  label: string;
  entries: Entry[];
  invoices: Array<{ id: string; number: string; workRecordDocumentId: string | null }>;
  expenses: Expense[];
}
interface Invoice {
  id: string;
  number: string;
  serviceMonth: string;
  invoiceDate: string;
  dueDate: string;
  paidAt: string | null;
  overdue: boolean;
  workRecordDocumentId: string | null;
}

const SOURCE: Record<string, string> = {
  task: 'Task',
  document: 'Document',
  deliverable: 'Deliverable',
  meeting: 'Meeting',
  report: 'Report',
};
const EXPENSE_TONE = { REQUESTED: 'warn', APPROVED: 'ok', DECLINED: 'bad', REIMBURSED: 'neutral' } as const;

/** Months from the engagement start through this month, newest first. */
function monthsSince(start: string, timeZone: string) {
  const out: string[] = [];
  let m = todayIn(timeZone).slice(0, 7);
  const first = start.slice(0, 7);
  while (m >= first && out.length < 36) {
    out.push(m);
    const d = new Date(`${m}-01T00:00:00Z`);
    d.setUTCMonth(d.getUTCMonth() - 1);
    m = d.toISOString().slice(0, 7);
  }
  return out;
}
const lastDay = (m: string) => {
  const d = new Date(`${m}-01T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + 1);
  return addDays(d.toISOString().slice(0, 10), -1);
};
const monthName = (m: string) =>
  new Date(`${m}-01T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });

export function WorkRecordPage() {
  const program = useOutletContext<Program>();
  const base = `/api/programs/${program.id}/work-record`;
  const allowed = program.capabilities.includes('workrecord.manage');
  const eng = useQuery({
    queryKey: ['work-record', program.id, 'engagement'],
    queryFn: () => api.get<Engagement>(`${base}/engagement`),
    enabled: allowed,
  });
  if (!allowed) return <Navigate to={`/p/${program.id}/tracker`} replace />;
  if (!eng.data) return <Spinner className="h-64" />;

  return (
    <div className="mx-auto max-w-[1200px] space-y-5 px-6 py-6">
      <PageHeader
        title="Work Record"
        description="BlackWolf only. The record of work performed, expense pre-approvals and invoice dates. Fees are never stored in HELM."
      />
      <EngagementCard e={eng.data} client={program.clientShortName} />
      <MonthLog program={program} months={monthsSince(eng.data.effectiveDate, program.timezone)} />
      <div className="grid gap-5 lg:grid-cols-2">
        <Invoices program={program} />
        <Expenses program={program} />
      </div>
    </div>
  );
}

function EngagementCard({ e, client }: { e: Engagement; client: string }) {
  const s = e.status;
  const soon = s.daysToNoticeDeadline <= 30;
  return (
    <Card className="grid gap-4 p-5 sm:grid-cols-2 lg:grid-cols-4">
      <Stat label={s.term === 1 ? 'Initial term' : `Renewal term ${s.term - 1}`}>
        {fmtDate(s.termStart)} to {fmtDate(s.termEnd)}
      </Stat>
      <Stat label="Renews on">{fmtDate(s.nextRenewal)}</Stat>
      <Stat label={`Last day for ${client} non-renewal notice`} className={soon ? 'text-warn' : ''}>
        {fmtDate(s.nonRenewalNoticeBy)}
        <span className="ml-1.5 text-xs text-muted">
          {s.daysToNoticeDeadline >= 0 ? `in ${s.daysToNoticeDeadline} days` : 'passed'}
        </span>
      </Stat>
      <Stat label="Terms">
        Invoice on day {e.invoiceDayOfMonth} for the prior month, due in {e.paymentTermsDays} days. Termination on{' '}
        {e.terminationNoticeDays} days' notice.
      </Stat>
      <p className="text-xs text-muted sm:col-span-2 lg:col-span-4">{e.agreementName}</p>
    </Card>
  );
}

function Stat({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div>
      <p className="text-xs text-muted">{label}</p>
      <p className={cn('mt-0.5 text-sm font-medium', className)}>{children}</p>
    </div>
  );
}

function MonthLog({ program, months }: { program: Program; months: string[] }) {
  const base = `/api/programs/${program.id}/work-record`;
  const [month, setMonth] = useState(months[0] ?? '');
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ date: '', description: '' });
  const qc = useQueryClient();
  const toast = useToast();
  const key = ['work-record', program.id, 'month', month];
  const q = useQuery({ queryKey: key, queryFn: () => api.get<Month>(`${base}/months/${month}`), enabled: !!month });
  // Load the month directly after each change and write it into the cache, so the table
  // never shows the pre-change list.
  const refresh = async () => {
    qc.setQueryData(key, await api.get<Month>(`${base}/months/${month}`));
    void qc.invalidateQueries({ queryKey: ['work-record', program.id, 'invoices'] });
  };

  const compile = useMutation({
    mutationFn: () => api.post<{ added: number; alreadyRecorded: number }>(`${base}/months/${month}/compile`),
    onSuccess: (r) => {
      toast.ok(r.added ? `${r.added} entr${r.added === 1 ? 'y' : 'ies'} added from activity.` : 'Nothing new to add.');
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const patch = useMutation({
    mutationFn: (v: { id: string; body: Record<string, unknown> }) => api.patch(`${base}/entries/${v.id}`, v.body),
    onSuccess: refresh,
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`${base}/entries/${id}`),
    onSuccess: refresh,
    onError: (e) => toast.error(errorMessage(e)),
  });
  const add = useMutation({
    mutationFn: () => api.post(`${base}/entries`, draft),
    onSuccess: () => {
      setAdding(false);
      setDraft({ date: '', description: '' });
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const exportPdf = useMutation({
    mutationFn: () => api.post<{ documentId: string; version: number }>(`${base}/months/${month}/export`),
    onSuccess: (r) => {
      toast.ok(`Record of work performed filed as version ${r.version}.`);
      refresh();
      void downloadVersion(program.id, r.documentId, r.version).catch((e) => toast.error(errorMessage(e)));
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const m = q.data;
  const included = m?.entries.filter((e) => !e.excluded).length ?? 0;
  return (
    <Card className="p-0">
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
        <h2 className="mr-2 text-sm font-semibold">Record of work performed</h2>
        <Select value={month} onChange={(e) => setMonth(e.target.value)} aria-label="Month" className="w-44">
          {months.map((x) => (
            <option key={x} value={x}>
              {monthName(x)}
            </option>
          ))}
        </Select>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => compile.mutate()} loading={compile.isPending}>
            <RefreshCw /> Compile from activity
          </Button>
          <Button variant="secondary" onClick={() => setAdding(true)}>
            <Plus /> Add entry
          </Button>
          <Button onClick={() => exportPdf.mutate()} loading={exportPdf.isPending} disabled={!included}>
            <FileDown /> Export PDF
          </Button>
        </div>
      </div>
      {!m ? (
        <Spinner className="h-32" />
      ) : !m.entries.length ? (
        <p className="px-4 py-6 text-sm text-muted">
          Nothing recorded for {m.label} yet. Compile from activity to pull in BlackWolf's tasks, filed documents,
          submissions, meetings and published reports, then add anything else by hand.
        </p>
      ) : (
        <table className="w-full text-[13px]">
          <thead className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted">
            <tr>
              <th className="w-20 px-4 py-2 font-medium">In record</th>
              <th className="w-32 px-2 py-2 font-medium">Date</th>
              <th className="px-2 py-2 font-medium">Work</th>
              <th className="w-28 px-2 py-2 font-medium">Source</th>
              <th className="w-10 px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {m.entries.map((e) => (
              <tr
                key={e.id}
                className={cn('border-b border-border last:border-0', e.excluded && 'text-muted line-through')}
              >
                <td className="px-4 py-2">
                  <input
                    type="checkbox"
                    checked={!e.excluded}
                    aria-label={`Include: ${e.description}`}
                    onChange={() => patch.mutate({ id: e.id, body: { excluded: !e.excluded } })}
                    className="size-4 accent-[var(--brand)]"
                  />
                </td>
                <td className="whitespace-nowrap px-2 py-2 tabular-nums">{fmtDate(e.date)}</td>
                <td className="px-2 py-2">{e.description}</td>
                <td className="px-2 py-2">
                  <Badge>{e.sourceType ? (SOURCE[e.sourceType] ?? e.sourceType) : 'Manual'}</Badge>
                </td>
                <td className="px-4 py-2">
                  {e.manual && (
                    <Button variant="ghost" size="icon" aria-label="Delete entry" onClick={() => remove.mutate(e.id)}>
                      <Trash2 />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {m && m.expenses.length > 0 && (
        <p className="border-t border-border px-4 py-2 text-xs text-muted">
          {m.expenses.length} approved expense{m.expenses.length === 1 ? '' : 's'} this month will print with the
          record.
        </p>
      )}
      <Dialog
        open={adding}
        onClose={() => setAdding(false)}
        title="Add work"
        footer={
          <>
            <Button variant="secondary" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => add.mutate()}
              loading={add.isPending}
              disabled={!draft.date || draft.description.trim().length < 3}
            >
              Add
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Field label="Date" htmlFor="wr-date">
            <Input
              id="wr-date"
              type="date"
              value={draft.date}
              min={`${month}-01`}
              max={lastDay(month)}
              onChange={(e) => setDraft({ ...draft, date: e.target.value })}
            />
          </Field>
          <Field label="Work performed" htmlFor="wr-desc">
            <Input
              id="wr-desc"
              value={draft.description}
              maxLength={500}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            />
          </Field>
        </div>
      </Dialog>
    </Card>
  );
}

function Invoices({ program }: { program: Program }) {
  const base = `/api/programs/${program.id}/work-record`;
  const qc = useQueryClient();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ number: '', serviceMonth: '', invoiceDate: '' });
  const q = useQuery({
    queryKey: ['work-record', program.id, 'invoices'],
    queryFn: () => api.get<Invoice[]>(`${base}/invoices`),
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['work-record', program.id] });
  const add = useMutation({
    mutationFn: () => api.post(`${base}/invoices`, draft),
    onSuccess: () => {
      setAdding(false);
      setDraft({ number: '', serviceMonth: '', invoiceDate: '' });
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const pay = useMutation({
    mutationFn: (v: { id: string; paidAt: string | null }) =>
      api.patch(`${base}/invoices/${v.id}`, { paidAt: v.paidAt }),
    onSuccess: refresh,
    onError: (e) => toast.error(errorMessage(e)),
  });
  const today = todayIn(program.timezone);

  return (
    <Card className="p-0">
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold">Invoices</h2>
        <span className="text-xs text-muted">Dates only</span>
        <Button variant="secondary" size="sm" className="ml-auto" onClick={() => setAdding(true)}>
          <Plus /> Record invoice
        </Button>
      </div>
      {!q.data ? (
        <Spinner className="h-24" />
      ) : !q.data.length ? (
        <p className="px-4 py-4 text-sm text-muted">No invoices recorded yet.</p>
      ) : (
        <ul className="divide-y divide-border">
          {q.data.map((i) => (
            <li key={i.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-[13px]">
              <span className="font-medium">{i.number}</span>
              <span className="text-muted">{monthName(i.serviceMonth)}</span>
              <span className="text-xs text-muted">
                Sent {fmtDate(i.invoiceDate)}, due {fmtDate(i.dueDate)}
              </span>
              <span className="ml-auto flex items-center gap-2">
                {i.paidAt ? (
                  <Badge tone="ok">Paid {fmtDate(i.paidAt)}</Badge>
                ) : (
                  <>
                    {i.overdue && <Badge tone="bad">Overdue</Badge>}
                    <Button size="sm" variant="secondary" onClick={() => pay.mutate({ id: i.id, paidAt: today })}>
                      Mark paid today
                    </Button>
                  </>
                )}
                {i.workRecordDocumentId ? (
                  <Badge tone="info">Record attached</Badge>
                ) : (
                  <Badge tone="warn">No record yet</Badge>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      <Dialog
        open={adding}
        onClose={() => setAdding(false)}
        title="Record an invoice"
        description="Number and dates only. The amount stays in your accounting system."
        footer={
          <>
            <Button variant="secondary" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => add.mutate()}
              loading={add.isPending}
              disabled={!draft.number.trim() || !draft.serviceMonth || !draft.invoiceDate}
            >
              Record
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Field label="Invoice number" htmlFor="inv-no">
            <Input id="inv-no" value={draft.number} onChange={(e) => setDraft({ ...draft, number: e.target.value })} />
          </Field>
          <Field label="Service month" htmlFor="inv-month" hint="The month the invoice bills for.">
            <Input
              id="inv-month"
              type="month"
              value={draft.serviceMonth}
              onChange={(e) => setDraft({ ...draft, serviceMonth: e.target.value })}
            />
          </Field>
          <Field label="Invoice date" htmlFor="inv-date" hint="The due date is computed from the payment terms.">
            <Input
              id="inv-date"
              type="date"
              value={draft.invoiceDate}
              onChange={(e) => setDraft({ ...draft, invoiceDate: e.target.value })}
            />
          </Field>
        </div>
      </Dialog>
    </Card>
  );
}

function Expenses({ program }: { program: Program }) {
  const base = `/api/programs/${program.id}/work-record`;
  const qc = useQueryClient();
  const toast = useToast();
  const docs = useDocuments(program.id);
  const q = useQuery({
    queryKey: ['work-record', program.id, 'expenses'],
    queryFn: () => api.get<Expense[]>(`${base}/expenses`),
  });
  const [editing, setEditing] = useState<Partial<Expense> | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['work-record', program.id] });
  const save = useMutation({
    mutationFn: (x: Partial<Expense>) => {
      const body = {
        description: x.description,
        amount: x.amount,
        status: x.status,
        approvedByName: x.approvedByName || null,
        approvedAt: x.approvedAt || null,
        approvalDocumentId: x.approvalDocumentId || null,
        receiptDocumentId: x.receiptDocumentId || null,
      };
      return x.id ? api.patch(`${base}/expenses/${x.id}`, body) : api.post(`${base}/expenses`, body);
    },
    onSuccess: () => {
      setEditing(null);
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const docName = (id: string | null) => docs.data?.find((d) => d.id === id)?.title;
  const set = (p: Partial<Expense>) => setEditing((x) => ({ ...x, ...p }));
  const needsApproval = editing?.status === 'APPROVED' || editing?.status === 'REIMBURSED';

  return (
    <Card className="p-0">
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold">Expense pre-approvals</h2>
        <Button
          variant="secondary"
          size="sm"
          className="ml-auto"
          onClick={() => setEditing({ status: 'REQUESTED', description: '', amount: '' })}
        >
          <Plus /> Request
        </Button>
      </div>
      {!q.data ? (
        <Spinner className="h-24" />
      ) : !q.data.length ? (
        <p className="px-4 py-4 text-sm text-muted">
          No expenses. Each one needs {program.clientShortName}'s written approval before it is incurred.
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {q.data.map((x) => (
            <li key={x.id}>
              <button
                type="button"
                onClick={() => setEditing(x)}
                className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-left text-[13px] hover:bg-surface-2/60"
              >
                <span className="flex-1">{x.description}</span>
                <span className="tabular-nums">${x.amount}</span>
                <Badge tone={EXPENSE_TONE[x.status]}>{x.status.toLowerCase()}</Badge>
                {x.approvedByName && (
                  <span className="w-full text-xs text-muted">
                    Approved by {x.approvedByName} on {fmtDate(x.approvedAt)}
                    {docName(x.approvalDocumentId) ? ` · ${docName(x.approvalDocumentId)}` : ''}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
      <Dialog
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? 'Expense' : 'Request an expense'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={() => editing && save.mutate(editing)} loading={save.isPending}>
              Save
            </Button>
          </>
        }
      >
        {editing && (
          <div className="space-y-3">
            <Field label="What it is for" htmlFor="ex-desc">
              <Input
                id="ex-desc"
                value={editing.description ?? ''}
                onChange={(e) => set({ description: e.target.value })}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Amount (USD)" htmlFor="ex-amt">
                <Input
                  id="ex-amt"
                  inputMode="decimal"
                  value={editing.amount ?? ''}
                  onChange={(e) => set({ amount: e.target.value })}
                />
              </Field>
              <Field label="Status" htmlFor="ex-status">
                <Select
                  id="ex-status"
                  value={editing.status}
                  onChange={(e) => set({ status: e.target.value as Expense['status'] })}
                >
                  <option value="REQUESTED">Requested</option>
                  <option value="APPROVED">Approved</option>
                  <option value="DECLINED">Declined</option>
                  <option value="REIMBURSED">Reimbursed</option>
                </Select>
              </Field>
            </div>
            {needsApproval && (
              <Alert tone="info">
                Approval needs {program.clientShortName}'s written approval filed in Documents first (an email or
                letter), who approved it and when.
              </Alert>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Approved by" htmlFor="ex-by">
                <Input
                  id="ex-by"
                  value={editing.approvedByName ?? ''}
                  onChange={(e) => set({ approvedByName: e.target.value })}
                />
              </Field>
              <Field label="Approved on" htmlFor="ex-on">
                <Input
                  id="ex-on"
                  type="date"
                  value={editing.approvedAt ?? ''}
                  onChange={(e) => set({ approvedAt: e.target.value })}
                />
              </Field>
            </div>
            <Field label="Written approval" htmlFor="ex-doc">
              <Select
                id="ex-doc"
                value={editing.approvalDocumentId ?? ''}
                onChange={(e) => set({ approvalDocumentId: e.target.value || null })}
              >
                <option value="">None filed</option>
                {docs.data?.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.title}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Receipt" htmlFor="ex-receipt">
              <Select
                id="ex-receipt"
                value={editing.receiptDocumentId ?? ''}
                onChange={(e) => set({ receiptDocumentId: e.target.value || null })}
              >
                <option value="">None filed</option>
                {docs.data?.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.title}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        )}
      </Dialog>
    </Card>
  );
}
